import { Fragment, useContext, type FC } from 'react';
import { CardBlock } from '@sbt-web/ancillary-services';
import { useViewport } from '@sbt-web/hooks';
import { OptimizelySubitoContext, OptimizelySubitoFeature } from '@sbt-web/houston-wrapper';
import { AdTypes, CategoryID, GeoEntry } from '@sbt-web/network/types';
import Geoboost from '@/client/components/Adv/Geoboost';
import { GEOBOOST_TOGGLE } from '@/client/components/Adv/Geoboost/shared';
import { prepareRemoteDataListing } from '@/shared/helpers/Hebe';
import { GeoboostItem, Item } from '@/shared/models';
import { getErrorMessage } from '@/tools/errorHelpers';
import type { FiltersStore } from '@/tools/search/getFiltersStore';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import { useAdvToggle } from '@/client/hooks/useAdvToggle';
import { isInterstitialEnabled } from '@/shared/adv/interstitial';
import { ExpandSearch } from '../Elements/ExpandSearch';
import GalleryCTA from '../Elements/GalleryCTA';
import { AdItem } from './AdItem';
import { addSmartADVItem } from './SmartAdvItem';
import { isThisPlacementEnabled } from './SmartAdvItem/helpers';
export interface ListingItemProps {
  item: Item;
  index: number;
  listingCategory: CategoryID;
  adType: AdTypes;
  filters: Record<string, string>;
  filtersLabels?: Record<string, string>;
  query: string;
  geo: GeoEntry;
  macroCategory: string;
  handleItemClick: () => void;
  filterStore: FiltersStore | null;
}
export const ListingItem: FC<ListingItemProps> = listingProps => {
  const {
    isMobile
  } = useViewport();
  const {
    optimizely
  } = useContext(OptimizelySubitoContext);
  const {
    noAdvToggle
  } = useAdvToggle();
  const props = {
    ...listingProps,
    isMobile
  };
  const {
    item
  } = props;
  const handleItemClick = () => {
    if (item.kind === 'GalleryItem') {
      optimizely?.track('click_on_vetrina_ad');
    }
    props.handleItemClick?.();
  };
  try {
    return <>
        {/* Add interstitial */}
        {isInterstitialEnabled(item) ? renderElement(props, isMobile, noAdvToggle, handleItemClick) : <div data-google-interstitial="false">
            {renderElement(props, isMobile, noAdvToggle, handleItemClick)}
          </div>}
      </>;
  } catch (error) {
    const {
      index,
      item: item_0
    } = props;
    console.error(`An error occurred while rendering the item at position ${index}`, getErrorMessage(error), JSON.stringify(item_0));
    return null;
  }
};
function renderElement(props: ListingItemProps, isMobile: boolean, noAdvToggle: string, handleItemClick: () => void) {
  const {
    item,
    index,
    listingCategory,
    adType,
    query,
    geo,
    filters,
    macroCategory
  } = props;
  switch (item.kind) {
    case 'AdItem':
      return <AdItem item={item} index={index} listingCategory={listingCategory} handleItemClick={handleItemClick} />;
    case 'SmartADVItem':
      if (!isThisPlacementEnabled(item, null)) {
        return;
      }
      return addSmartADVItem(item, listingCategory, isMobile);
    case 'CardBlockItem':
      if (noAdvToggle !== 'B') return addCardBlockItem({
        ...props,
        isMobile
      });
      return;
    case 'GalleryPlaceholder':
      return;
    case 'GalleryCTAItem':
      return addGalleryCTAItem(isMobile);
    case 'GalleryItem':
      return <AdItem item={item.adItem} index={index} listingCategory={listingCategory} handleItemClick={handleItemClick} />;
    case 'GeoboostItem':
      return addGeoboostItem(item);
    case 'DecoratedItem':
      return <Fragment key={`${index}-decorated`}>
          {item.before.map((plugin, i) => <div className="items__item" key={`before_${index}_${i}`}>
              <ListingItem item={plugin.model} index={index} listingCategory={listingCategory} adType={adType} filters={filters} query={query} geo={geo} macroCategory={macroCategory} handleItemClick={handleItemClick} filterStore={props.filterStore} />
            </div>)}
          <AdItem item={item.item} index={index} listingCategory={listingCategory} handleItemClick={handleItemClick} />
          {item.after.map((plugin, i) => <div id={`ad_wrapper_${index}_${i}`} className="items__item" key={`after_${index}_${i}`}>
              <ListingItem item={plugin.model} index={index} listingCategory={listingCategory} adType={adType} filters={filters} query={query} geo={geo} macroCategory={macroCategory} handleItemClick={handleItemClick} filterStore={props.filterStore} />
            </div>)}
        </Fragment>;
    case 'ExpandSearchItem':
      return <ExpandSearch key="expand-search" withContainer />;
  }
}
ListingItem.displayName = 'ListingItem';
function addCardBlockItem(props: ListingItemProps & {
  isMobile: boolean;
}) {
  const {
    listingCategory,
    query,
    geo,
    filters,
    isMobile,
    filterStore
  } = props;
  const {
    adType
  } = props;
  return <CardBlock flex reverse pulseInstance={getOrCreatePulse()} border remoteData={prepareRemoteDataListing('NINTH_POSITION', adType, listingCategory, geo, filters, query, isMobile, filterStore)} />;
}
function addGalleryCTAItem(isMobile: boolean) {
  return !isMobile && <GalleryCTA key="gallery-cta" />;
}
function addGeoboostItem(item: GeoboostItem) {
  if (!item.boostedItems.length) {
    return null;
  }
  return <OptimizelySubitoFeature feature={GEOBOOST_TOGGLE}>
      {isEnabled => {
      return isEnabled /* NOSONAR */ ? <Geoboost items={item.boostedItems} /> : null;
    }}
    </OptimizelySubitoFeature>;
}